/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2013, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * @module dashboard/data/Model
 * @see Model
 */
define([
	'underscore',
	'../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../lib/@waca/core-client/js/core-client/ui/core/Events',
	'./MultilingualAttribute'
], function (_, Class, Events, MultilingualAttribute) {

	// Private Methods and Variables
	// -----------------------------
	var idCounter = 0;
	var lastIdDate = (new Date()).valueOf();

	/**
	 * Generates a UniqueID
	 *
	 * @param {String} prefix
	 * @returns {String} An ID
	 * @private
	 */
	function generateUniqueId(prefix) {
		var date = (new Date()).valueOf();
		if (date !== lastIdDate) {
			idCounter = 0;
			lastIdDate = date;
		} else {
			idCounter++;
		}

		var datePart = ('0000000000000000' + date.toString(16)).slice(-16);
		var countPart = ('00000000' + idCounter.toString(16)).slice(-8);

		return [prefix, datePart, '_', countPart].join('');
	}

	function clone(o) {
		try {
			return JSON.parse(JSON.stringify(o));
		} catch(e) {
			throw new Error(`Error attempting to clone object:\n${e.message}`);
		}
	}


	/**
	 * @param {object}
	 *            attrs Any properties to be mixed-in with this instance
	 * @property {String} id An id for this model. This will be generated if not specified
	 * @class
	 * @classdesc A Backbone style model class
	 */
	var Model = null;
	Model = Class.extend([Events], {

		/**
		 * The nested model structure allows a model to contain other models. For example,
		 * if you have a model Foo and it contains a model Bar then you can define your
		 * model as follows:
		 *
		 * var FooModel = Model.extend({
		 *		nestedModels: { bar: BarModel },
		 *  ...
		 * });
		 *
		 * Users of the FooModel can now be notified when a 'bar' changes and serialize the FooModel
		 * as JSON.
		 */
		nestedModels: {},

		/**
		 * The nested collections structure allows a model to contain collections. For example,
		 * if you have a model Foo and it contains a collection Bars then you can define your
		 * model as follows:
		 *
		 * var FooModel = Model.extend({
		 *		nestedCollections: { bars: BarsCollection },
		 *  ...
		 * });
		 *
		 * Users of the FooModel can now be notified when 'bars' changes and serialize the FooModel
		 * as JSON.
		 */
		nestedCollections: {},

		/**
		 * The localized property structure allows a model to be aware that certain properties can
		 * be translated and have multiple languages. Adding an attribute to your model localized props
		 * allows it to set the value for new entries in the proper way.
		 *
		 * Under the hood strings are represented in a map of languages:
		 *
		 * translationTable: {
		 * 	'en': 'This is my English string',
		 * 	'fr': 'This is my French string'
		 * }
		 *
		 * The TranslationService takes care of managing these tables and performing CRUD operations on them
		 *
		 */
		localizedProps: [],

		whitelistAttrs: [],

		/**
		 * Lists of attributes that are allowed to have "runtime only" values.
		 * This list should be the minimum required since a second copy of the values is kept at all times.
		 */
		runtimeAttrs: [],

		init: function (attrs, options) {
			options = options || {};
			Model.inherited('init', this, arguments);

			// save space by not setting _persistedRuntimeAttrs if we are not going to use it.
			if (this.runtimeAttrs.length) {
				this._persistedRuntimeAttrs = {};
			}
			this.set(attrs, _.defaults(options, {
				silent: true
			}));

			if (this.id === null || this.id === undefined) {
				// generate unique id
				this.id = this._generateUniqueId('model');
			}

			/**
			 * Array of object describing model properties that should be included in the CM property 'references'
			 * Object.name - The name of the property
			 * Object.type - The type of the object being referenced (palette, image, ...)
			 * Object.get - Optional, if defined will be used to get the value of the property instead of calling Model.get
			 */
			this.contentReferences = [];
			this.colorProperties = [];
		},

		trigger: function(eventname, event) {
			if(event && event.data && event.data.transactionToken && event.data.transactionToken.source === 'event') {
				// The API event is a frozen object that connect be modified. we need to clone it if we want to propagate a modified transaction token
				event.data.transactionToken = JSON.parse(JSON.stringify(event.data.transactionToken));
				event.data.transactionToken.eventGenerated = true;
			}
			return Model.inherited('trigger', this, arguments);
		},

		/**
		 * Generates a unique id
		 *
		 * @param {string}  Prefix used in the generated id.
		 * @protected
		 * @returns {String} The id
		 */
		_generateUniqueId: function (prefix) {
			return generateUniqueId(prefix);
		},

		/**
		 * Used to protect against model attribute that conflict with existing methods
		 * We will change the property name internally when we store and restore it when we serialize the json
		 *
		 */
		resolvePropertyName: function (name, value) {
			if (value && (typeof this[name]) === (typeof value)) {
				// If the type of the value is the same as the one that exists in the mode,
				// then we resolve to the same name
				return name;
			}

			return (typeof this[name] === 'function') ? '_' + name + 'Prop' : name;
		},

		/**
		 * Gets the ID
		 *
		 * @returns {String} The id
		 */
		getId: function () {
			return this.id;
		},

		/**
		 * Creates a copy of the attributes in the whitelistAttrs array. These are typically the attributes you want to persist
		 *
		 * N.B. toJSON is not a great name because this returns an object and not a JSON string, but unfortunately this is how
		 * JSON.stringify() works.
		 * @param additionalAttrs - an optional array of attributes which can be used to augment the returned JSON with attributes not in the whitelist
		 * @param excludedAttrs - an optional array of attributes which will be ignored from the whitelist. Allows derived class to serialize some properties themselves.
		 * @returns a copy of this model's attributes specified in whitelistAttrs
		 */
		toJSON: function (additionalAttrs, excludedAttrs) {
			var obj = {};

			var getJson = function (value) {
				var json;
				if (!value) {
					json = value;
				} else {
					if (value.toJSON && _.isFunction(value.toJSON)) {
						json = value.toJSON();
					} else if (_.isArray(value)) {
						json = _.map(value, function (v) {
							return getJson(v);
						});
					} else {
						json = clone(value);
					}
				}
				return json;
			};


			var filteredWhitelist = this.whitelistAttrs;
			if (excludedAttrs) {
				filteredWhitelist = this.whitelistAttrs.filter(function (attr) {
					return !_.contains(excludedAttrs, attr);
				});
			}
			_.each(filteredWhitelist, function (attr) {
				if (this.runtimeAttrs.indexOf(attr) !== -1) {
					if (this._persistedRuntimeAttrs[attr] !== undefined) {
						obj[attr] = getJson(this._persistedRuntimeAttrs[this.resolvePropertyName(attr)]);
					}
				} else {
					if (this[attr] !== undefined) {
						obj[attr] = getJson(this[this.resolvePropertyName(attr)]);
					}
				}
			}.bind(this));

			if (additionalAttrs) {
				_.each(additionalAttrs, function (attr) {
					if (this[this.resolvePropertyName(attr)] !== undefined) {
						obj[attr] = getJson(this[this.resolvePropertyName(attr)]);
					}
				}.bind(this));
			}

			return obj;
		},

		getUsedCustomColors: function (customColors, colorProperties = []) {
			let usedColors = [];

			if (!customColors) {
				return;
			}

			// Loops through nested collections
			for (let collection in this.nestedCollections) {
				if (this[collection]) {
					const models = this[collection].getModels();
					models.forEach((model) => {
						usedColors = usedColors.concat(model.getUsedCustomColors(customColors));
					});
				}
			}

			// Loop through our nested models
			for (let model in this.nestedModels) {
				if (this[model]) {
					usedColors = usedColors.concat(this[model].getUsedCustomColors(customColors));
				}
			}

			const colorPropertiesToCheck = this.colorProperties.length > 0 ? this.colorProperties : colorProperties;
			colorPropertiesToCheck.forEach((propName) => {
				let val = this.get(propName);
				customColors.ids.forEach((color, index) => {
					if (val && val.indexOf(color) !== -1) {
						usedColors.push(customColors.fills[index]);
					}
				});

			});

			// We need a unique list of deployment references
			return _.uniq(usedColors, false);
		},


		/**
		 * Get all the values for the CM 'references' property.
		 * @return {Array}  returns an array of objects.  Object.value and Object.type
		 */
		getContentReferences: function () {
			let deploymentRefs = [];

			// Loops through nested collections
			for (let collection in this.nestedCollections) {
				if (this[collection]) {
					const models = this[collection].getModels();
					models.forEach((model) => {
						deploymentRefs = deploymentRefs.concat(model.getContentReferences());
					});
				}
			}

			// Loop through our nested models
			for (let model in this.nestedModels) {
				if (this[model]) {
					deploymentRefs = deploymentRefs.concat(this[model].getContentReferences());
				}
			}

			// Loop through all the deployment references
			this.contentReferences.forEach((refInfo) => {
				const propValue = refInfo.get ? refInfo.get() : this.get(refInfo.name);
				if (propValue) {
					deploymentRefs.push({
						value: propValue,
						type: refInfo.type
					});
				}
			});

			// We need a unique list of deployment references
			return _.uniq(deploymentRefs, false, (ref) => {
				return ref.value;
			});
		},

		/**
		 * Sets a collection property on the model
		 *
		 * @param	name			The property name for the nested collection
		 * @param	value			The collection contents to set
		 * @param	options			Options to pass to the nested collection set
		 */
		_setCollection: function (propName, value, options) {
			var name = this.resolvePropertyName(propName, value);
			if (this[name] && typeof this[name].set === 'function') {
				// collection already exists
				this[name].reset(value, options);
			} else {
				var collection;
				if (value instanceof this.nestedCollections[name]) {
					// referencing the collection that's passed-in
					collection = value;
				} else {
					collection = new this.nestedCollections[name](value, options);
				}
				this[name] = collection;

				var fGetFromOptions = function (event, sName) {
					return event[sName] || (event.options ? event.options[sName] : null);
				};

				collection.on('all', function (event) {
					if (!event || event.stopPropagation) {
						// nested collection sent some sort of custom event
						return;
					}
					var senderContext = fGetFromOptions(event, 'senderContext');
					var sender = fGetFromOptions(event, 'sender');

					// something changed in the collection
					var eventName = 'change:' + name;
					var modelEvent = {
						eventName: eventName,
						name: event.name,
						value: event.value,
						prevValue: event.prevValue,
						model: this,
						data: event.data || (event.options ? event.options.payloadData : null),
						collection: this.collection || null,
						sender: sender,
						senderContext: senderContext,
						origCollectionEvent: event
					};
					this.trigger(eventName, modelEvent);

				}.bind(this));
			}
		},

		/**
		 * Sets a nested model property on the model
		 *
		 * @param	name			The property name for the nested model
		 * @param	value			The model contents to set
		 * @param	options			Options to pass to the nested model set
		 */
		_setNestedModel: function (propName, value, options) {
			var name = this.resolvePropertyName(propName, value);
			if (this[name] && typeof this[name].set === 'function') {
				// model already exists
				this[name].set(value, options);
			} else {
				var model;
				if (value instanceof this.nestedModels[name]) {
					// referencing the collection that's passed-in
					model = value;
				} else {
					model = new this.nestedModels[name](value, options);
				}

				this[name] = model;
				model.on('all', function (event) {
					if (!event || event.stopPropagation) {
						// nested model sent some sort of custom event
						return;
					}
					// something changed in the model
					var eventName = 'change:' + name;
					var modelEvent = {
						eventName: eventName,
						name: event.name,
						value: event.value,
						prevValue: event.prevValue,
						model: this,
						sender: event.sender || (event.options ? event.options.sender : null),
						senderContext: event.senderContext || (event.options ? event.options.senderContext : null),
						origModelEvent: event,
						data: event.data || (event.options ? event.options.payloadData : null)
					};
					this.trigger(eventName, modelEvent);

				}.bind(this));
			}
		},

		/**
		 * @param {String} propName - name of the property
		 * @param {Object} objOrValue - either the string value to set or an object containing a value and locale property
		 * @param {Object} options
		 */
		_setLocalized: function (propName, objOrValue, options) {
			objOrValue = objOrValue === null ? undefined : objOrValue;
			let value = null;
			let locale = null;

			if (typeof (objOrValue) === 'object' && !objOrValue.translationTable) {
				if (objOrValue.locale) {
					locale = objOrValue.locale;
				}
				if (objOrValue.value) {
					value = objOrValue.value;
				}
			} else {
				value = objOrValue;
			}

			const name = this.resolvePropertyName(propName, value);

			if (!locale) {
				if (this[name]) {
					locale = this[name].getCurrentModelLocale();
				} else {
					locale = options.defaultLocale || 'Default';
				}
			}

			const forceEvent = options.payloadData ? options.payloadData.forceEvent : false;

			const prevValue = this[name] ? this[name].getValue(locale) : null;
			if (prevValue !== value || forceEvent) {
				if (this[name]) {
					this[name].setValue(value, locale);
				} else {
					this[name] = new MultilingualAttribute(value, locale, options);
				}

				if (!options.silent || forceEvent) {
					const eventName = 'change:' + name;
					const event = {
						eventName: eventName,
						name: name,
						// call getValue() again to get the new string to display
						value: this[name].getValue(),
						upValue: {
							value: value,
							locale: locale
						},
						prevValue: {
							value: prevValue,
							locale: locale
						},
						model: this,
						collection: this.collection || null,
						sender: options.sender || this,
						senderContext: options.senderContext,
						data: options.payloadData
					};
					this.trigger(eventName, event);
				}
			}
		},

		_setValue: function (propName, value, options) {
			var name = this.resolvePropertyName(propName, value);
			var forceEvent = options.payloadData ? options.payloadData.forceEvent : false;
			var isRuntimeOnly = options.payloadData ? options.payloadData.runtimeOnly : false;
			var prev = this[name];
			if (prev !== value || forceEvent) {
				this[name] = value;
				if (!options.silent || forceEvent) {
					var eventName = 'change:' + name;
					var event = {
						eventName: eventName,
						name: name,
						value: value,
						prevValue: prev,
						model: this,
						collection: this.collection || null,
						sender: options.sender || this,
						senderContext: options.senderContext,
						data: options.payloadData
					};
					this.trigger(eventName, event);
				}
			}
			if (!isRuntimeOnly && this.runtimeAttrs.indexOf(name) !== -1) {
				this._persistedRuntimeAttrs[name] = value;
			}
		},

		/**
		 * Sets a property. See public method
		 *
		 * @protected
		 * @param name
		 * @param value
		 * @param options
		 */
		_set: function (name, value, options) {
			if (this.nestedCollections[name]) {
				this._setCollection(name, value, options);
			} else if (this.nestedModels[name]) {
				this._setNestedModel(name, value, options);
			} else {
				options = options ? _.clone(options) : {};
				options.senderContext = (options.senderContext) ? options.senderContext : {
					applyFn: this.applyFn.bind(this)
				};
				if (this.localizedProps.indexOf(name) !== -1) {
					this._setLocalized(name, value, options);
				} else {
					this._setValue(name, value, options);
				}
			}
		},

		/**
		 * generic set method, calls either setter or default _set
		 *
		 * @param values        values to set
		 * @param options       Options to pass along with the set. Some options are:
		 * 		options.silent	Set to true if events should not be triggered during the set
		 * 		options.sender	The sender of the set action
		 * 		options.forceEvent Forces a change event to fire even if trying to set the same value. Overrides options.silent
		 * 		options.payloadData	Any data to add to the events triggered by the set
		 * @returns {Model}
		 *
		 */
		set: function (values, options) {

			if ((typeof values === 'object') && (values !== null)) {
				for (var name in values) {
					if (values.hasOwnProperty(name)) {
						this._set(name, values[name], options);
					}
				}
			}

			return this;
		},

		/**
		 * Applies model change using set
		 *
		 * @param value
		 * @param sender
		 * @param name
		 */
		applyFn: function (value, sender, name, payloadData) {
			if (name) {
				var modelJson = {};
				modelJson[name] = value;
				var options = {
					sender: sender
				};
				if (payloadData) {
					options.payloadData = payloadData;
				}
				this.set(modelJson, options);
			}
		},

		/**
		 * Generic getter
		 *
		 * @param name
		 * @returns
		 */
		get: function (name) {
			var resolvedName = this.resolvePropertyName(name);
			if (this.localizedProps.indexOf(resolvedName) !== -1) {
				return this[resolvedName] && this[resolvedName].getValue();
			}
			return this[resolvedName];
		},

		/**
		 * Returns the persisted value if there is one.
		 *
		 * @param name
		 * @returns
		 */
		getPersisted: function (name) {
			if (this.runtimeAttrs.indexOf(name) !== -1) {
				return this._persistedRuntimeAttrs[name];
			} else {
				return this.get(name);
			}
		},

		/*
		 * Used while cloning a 'Model'; Regenerate and replace all IDs self and for all children.
		 */
		replaceIds: function (map) {
			if (this.id) {
				var newId = map[this.id];
				if (!newId) {
					newId = this._generateUniqueId('model');
					map[this.id] = newId;
				}
				this.from = this.id;
				this.id = newId;
			}
			_.each(this.items, function (item) {
				if (item.replaceIds) {
					item.replaceIds(map);
				}
			});
		},

		/**
		 * Given a property name, will return information about it's multilingualAttribute object
		 *
		 */
		getMultilingualAttribute: function (propertyName) {
			if (this.localizedProps.indexOf(propertyName) === -1 || !this[propertyName]) {
				return null;
			}

			return {
				propertyName: propertyName,
				propertyParentModel: this,
				multilingualProperty: this[propertyName]
			};
		},

		/**
		 * Returns all the multilingual properties from this model
		 */
		getMultilingualAttributes: function () {
			const multilingualAttributes = [];
			this.localizedProps.forEach((propName) => {
				const multilingualAttribute = this.getMultilingualAttribute(propName);
				if (multilingualAttribute) {
					multilingualAttributes.push(multilingualAttribute);
				}
			});

			return multilingualAttributes;
		}
	});

	return Model;
});